import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { VendorComplianceItem } from '@/types/domain/vendor-management';
import { AlertTriangle, CheckCircle2, FileText, XCircle } from 'lucide-react';

interface VendorCompliancePanelProps {
  complianceItems: VendorComplianceItem[];
}

export const VendorCompliancePanel: React.FC<VendorCompliancePanelProps> = ({ complianceItems }) => {
  return (
    <Card className="border-border rounded-2xl shadow-2xs font-heading">
      <CardHeader className="pb-3 border-b border-border/60">
        <CardTitle className="font-heading text-xs font-bold text-foreground uppercase tracking-wider flex items-center justify-between">
          <span>Compliance & Certification Tracker</span>
          <span className="text-[10px] font-mono text-primary font-bold">{complianceItems.length} Registered Certificates</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-3">
        {complianceItems.map((item) => (
          <div key={item.id} className="p-3.5 rounded-xl border border-border/80 bg-card space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-bold text-foreground">
                <FileText className="h-4 w-4 text-primary" />
                <span>{item.documentType}</span>
              </div>

              <span
                className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full flex items-center gap-1 ${
                  item.status === 'VERIFIED'
                    ? 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/20'
                    : item.status === 'EXPIRING_SOON'
                    ? 'bg-amber-500/10 text-amber-700 border border-amber-500/20'
                    : 'bg-rose-500/10 text-rose-700 border border-rose-500/20'
                }`}
              >
                {item.status === 'VERIFIED' && <CheckCircle2 className="h-3 w-3" />}
                {item.status === 'EXPIRING_SOON' && <AlertTriangle className="h-3 w-3" />}
                {item.status === 'EXPIRED' && <XCircle className="h-3 w-3" />}
                {item.status.replace(/_/g, ' ')}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2 text-[11px] text-foreground-secondary font-medium">
              <div>
                <span className="text-[10px] font-bold text-foreground-muted uppercase block">Document Ref</span>
                <span className="font-mono font-bold text-foreground">{item.documentNumber}</span>
              </div>

              <div>
                <span className="text-[10px] font-bold text-foreground-muted uppercase block">Expiry Date</span>
                <span className="font-mono font-bold text-foreground">{item.expiryDate}</span>
              </div>

              <div>
                <span className="text-[10px] font-bold text-foreground-muted uppercase block">Validity Tracker</span>
                <span
                  className={`font-bold ${
                    item.daysRemaining <= 30 ? 'text-amber-600 font-extrabold' : 'text-emerald-600'
                  }`}
                >
                  {item.daysRemaining > 0 ? `${item.daysRemaining} days remaining` : 'EXPIRED'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
